/*
 * Frame grid: zoom each product photo so the frame fills its picture box (3 Nov 2026).
 * The photos are squares with different amounts of white space round the frame, so one
 * fixed zoom crops some and leaves others small. This measures where the frame sits in a
 * small copy of the photo and scales the photo to fill the box, clear of the badges at the
 * top and the dots at the bottom. Photos without white space (lifestyle shots) are left alone.
 */
(() => {
  const ROOT = '.mbf';
  const PAD = { top: 0.08, bottom: 0.11, side: 0.035 }; // share of the box kept clear
  const MAX_ZOOM = 1.7;
  const boxes = new Map(); // src -> Promise<{x0,y0,x1,y1} as fractions, or null>

  function smallSrc(src) {
    const url = new URL(src, location.href);
    url.searchParams.set('width', '240');
    return url.toString();
  }

  function measure(src) {
    if (boxes.has(src)) return boxes.get(src);
    const job = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          const w = probe.naturalWidth, h = probe.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(probe, 0, 0);
          const px = ctx.getImageData(0, 0, w, h).data;
          let x0 = w, y0 = h, x1 = -1, y1 = -1;
          for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
              const i = (y * w + x) * 4;
              if (px[i + 3] < 24) continue;
              if (Math.min(px[i], px[i + 1], px[i + 2]) > 238) continue;
              if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
            }
          }
          if (x1 < 0) return resolve(null);
          const box = { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h };
          // Nothing to trim: a full-bleed photo.
          if (box.x1 - box.x0 > 0.97 && box.y1 - box.y0 > 0.97) return resolve(null);
          resolve(box);
        } catch (e) { resolve(null); }
      };
      probe.onerror = () => resolve(null);
      probe.src = smallSrc(src);
    });
    boxes.set(src, job);
    return job;
  }

  async function fit(img) {
    const src = img.currentSrc || img.src;
    if (!src) return;
    const box = await measure(src);
    const holder = img.closest('.mbf-card__image-slide') || img.closest('.mbf-card__image-bg') || img.parentElement;
    if (!box || !holder || !img.naturalWidth) { img.style.removeProperty('transform'); return; }
    const BW = holder.clientWidth, BH = holder.clientHeight;
    const ew = img.offsetWidth, eh = img.offsetHeight;
    if (!BW || !BH || !ew || !eh) return;
    // Where the photo is drawn inside the img element (object-fit: contain).
    const s0 = Math.min(ew / img.naturalWidth, eh / img.naturalHeight);
    const dw = img.naturalWidth * s0, dh = img.naturalHeight * s0;
    const ox = (ew - dw) / 2, oy = (eh - dh) / 2;
    // The img element's place in the holder (centred).
    const ex = (BW - ew) / 2, ey = (BH - eh) / 2;
    const cx0 = ox + box.x0 * dw, cy0 = oy + box.y0 * dh;
    const cw = (box.x1 - box.x0) * dw, ch = (box.y1 - box.y0) * dh;
    const availW = BW * (1 - 2 * PAD.side), availH = BH * (1 - PAD.top - PAD.bottom);
    const k = Math.max(1, Math.min(MAX_ZOOM, availW / cw, availH / ch));
    // Centre of the frame (element coords) goes to the centre of the clear area.
    const tcx = BW / 2 - ex, tcy = BH * PAD.top + availH / 2 - ey;
    const tx = tcx - (cx0 + cw / 2) * k, ty = tcy - (cy0 + ch / 2) * k;
    img.style.transformOrigin = '0 0';
    img.style.transform = `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(${k.toFixed(3)})`;
  }

  function fitAll(scope) {
    scope.querySelectorAll(`${ROOT} .mbf-card__image`).forEach((img) => {
      if (img.complete && img.naturalWidth) fit(img);
      else img.addEventListener('load', () => fit(img), { once: true });
    });
  }

  function start() {
    fitAll(document);
    let timer;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(() => fitAll(document), 150); });
    // Cards added later (e.g. "Show more frames").
    new MutationObserver((list) => {
      if (list.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('.mbf-card, .mbf-card__image') || n.querySelector?.('.mbf-card__image'))))) fitAll(document);
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
